import CharacterCount from '@tiptap/extension-character-count';
import Document from '@tiptap/extension-document';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';

import { useTemplateContextFieldsQuery } from '@/services/api/templatesV2ServiceEnhanced';

import { MergeFieldExtension } from './merge-field-extension';
import { createMergeFieldSuggestion } from './merge-field-suggestion';
import type { FieldMeta } from './serialization';
import { jinjaToHtml, tiptapToPlainText } from './serialization';

interface SmsTemplateEditorProps {
  initialBody: string;
  onBodyChange: (body: string) => void;
  readOnly?: boolean;
}

export const SmsTemplateEditor = ({
  initialBody,
  onBodyChange,
  readOnly = false,
}: SmsTemplateEditorProps) => {
  const { data: contextFields } = useTemplateContextFieldsQuery();
  const onChangeRef = useRef(onBodyChange);
  onChangeRef.current = onBodyChange;

  const fieldMap = useMemo(() => {
    if (!contextFields) return undefined;
    const map: Record<string, FieldMeta> = {};
    contextFields.categories.forEach((cat) => {
      cat.fields.forEach((f) => {
        map[f.key] = { label: f.label, sample: f.sample };
      });
    });
    return map;
  }, [contextFields]);

  const editor = useEditor(
    {
      extensions: [
        Document,
        Paragraph,
        Text,
        CharacterCount,
        MergeFieldExtension,
        ...(contextFields
          ? [
              Mention.configure({
                suggestion: createMergeFieldSuggestion(
                  contextFields.categories,
                ) as any,
              }),
            ]
          : []),
      ],
      content: jinjaToHtml(initialBody, fieldMap),
      editable: !readOnly,
      onUpdate: ({ editor: ed }) => {
        const plainText = tiptapToPlainText(ed.getJSON());
        onChangeRef.current(plainText);
      },
    },
    [contextFields, fieldMap, readOnly],
  );

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  if (!editor) return null;

  const charCount = editor.storage.characterCount?.characters() ?? 0;
  const SMS_SEGMENT_SIZE = 160;
  const segments = Math.ceil(charCount / SMS_SEGMENT_SIZE) || 1;

  return (
    <div className="flex h-full flex-col">
      {/* Header hint */}
      {!readOnly && (
        <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
          <span className="text-[11px] font-medium text-zinc-500">
            Plain text only — type{' '}
            <kbd className="rounded border px-1 py-0.5 text-[10px]">{'{'}</kbd>{' '}
            to insert variable
          </span>
          <span className="text-[11px] font-medium tabular-nums text-zinc-500">
            {charCount} chars · {segments} segment{segments !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4">
        <EditorContent
          editor={editor}
          className="max-w-none font-mono text-sm focus:outline-none [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:outline-none"
        />
      </div>
    </div>
  );
};
